import { toElementStyles } from '@genesislcap/foundation-utils';
import { Observable } from '@microsoft/fast-element';
import { assetPredicate, registrationPredicate, targetIdPredicate } from './utils';
/**
 * @internal
 */
export class AppStylesTargetBehavior {
    constructor(app, targetId, predicate) {
        this.app = app;
        this.targetId = targetId;
        this.predicate = predicate;
    }
    bind(elementInstance) {
        this.targetElement = elementInstance;
        Observable.getNotifier(this.app).subscribe(this, 'styles');
        this.handleChange();
    }
    unbind() {
        var _a;
        Observable.getNotifier(this.app).unsubscribe(this, 'styles');
        this.prevTargetStyles &&
            ((_a = this.targetElement) === null || _a === void 0 ? void 0 : _a.$fastController.removeStyles(this.prevTargetStyles));
    }
    handleChange() {
        var _a, _b;
        this.updateTargetStyles();
        if (this.prevTargetStyles !== this.nextTargetStyles) {
            this.prevTargetStyles &&
                ((_a = this.targetElement) === null || _a === void 0 ? void 0 : _a.$fastController.removeStyles(this.prevTargetStyles));
        }
        (_b = this.targetElement) === null || _b === void 0 ? void 0 : _b.$fastController.addStyles(this.nextTargetStyles);
    }
    updateTargetStyles() {
        this.prevTargetStyles = this.nextTargetStyles;
        this.nextTargetStyles = toElementStyles(this.app.styles
            .filter((asset) => targetIdPredicate(asset, this.targetId) &&
            assetPredicate(asset, this.app.config.context) &&
            registrationPredicate(asset, this.predicate, this.app.config.context))
            .map((token) => token.styles)
            .flat());
    }
}
/**
 * @internal
 */
const matchingFilterResult = (prev, next) => prev.length === next.length && prev.every((element, index) => element === next[index]);
/**
 * @internal
 */
export class AppRoutesBehavior {
    constructor(app, routeCollection, predicate) {
        this.app = app;
        this.routeCollection = routeCollection;
        this.predicate = predicate;
        this.prevRoutes = [];
        this.nextRoutes = [];
    }
    bind() {
        Observable.getNotifier(this.app).subscribe(this, 'routes');
        this.handleChange();
    }
    unbind() {
        Observable.getNotifier(this.app).unsubscribe(this, 'routes');
    }
    handleChange() {
        this.updateTargetRoutes();
        /**
         * There is no remove or unmap api on the router to do something like this:
         *
         * if (this.prevRoutes?.length) {
         *    this.routeCollection.unmap(...this.prevRoutes);
         * }
         *
         * ...but maybe we can add that eventually.
         */
        if (matchingFilterResult(this.prevRoutes, this.nextRoutes)) {
            return;
        }
        const newRoutes = this.nextRoutes.filter((route) => !this.prevRoutes.includes(route));
        if (newRoutes.length) {
            this.routeCollection.map(...newRoutes);
        }
    }
    updateTargetRoutes() {
        this.prevRoutes = this.nextRoutes;
        this.nextRoutes = this.app.routes.filter((asset) => assetPredicate(asset, this.app.config.context) &&
            registrationPredicate(asset, this.predicate, this.app.config.context));
    }
}
/**
 * @internal
 */
export class AppStoresBehavior {
    constructor(app, storeRoot, predicate) {
        this.app = app;
        this.storeRoot = storeRoot;
        this.predicate = predicate;
        this.prevStores = [];
        this.nextStores = [];
    }
    bind() {
        Observable.getNotifier(this.app).subscribe(this, 'stores');
        this.handleChange();
    }
    unbind() {
        Observable.getNotifier(this.app).unsubscribe(this, 'stores');
    }
    handleChange() {
        var _a, _b;
        this.updateTargetStores();
        if (matchingFilterResult(this.prevStores, this.nextStores)) {
            return;
        }
        if ((_a = this.prevStores) === null || _a === void 0 ? void 0 : _a.length) {
            const oldFragments = this.prevStores.map((appStore) => appStore.store);
            this.storeRoot.removeStoreFragments(...oldFragments);
        }
        if ((_b = this.nextStores) === null || _b === void 0 ? void 0 : _b.length) {
            const newFragments = this.nextStores.map((appStore) => appStore.store);
            this.storeRoot.addStoreFragments(...newFragments);
        }
    }
    updateTargetStores() {
        this.prevStores = this.nextStores;
        this.nextStores = this.app.stores.filter((asset) => assetPredicate(asset, this.app.config.context) &&
            registrationPredicate(asset, this.predicate, this.app.config.context));
    }
}
